import type { InsuranceCarrier } from "./insurance";
import { PracticeAddress } from "./practice";
import type { Preferences } from "./profile";
import type { ProfileCommon } from "./profileCommon";
import type { Role } from "./roles";
import type { UUID, Weekday } from "./common";

export type ProviderType =
  | "BEHAVIORAL"
  | "BEHAVIORAL_INTAKE"
  | "CASE_MANAGER"
  | "CASE_MANAGER_SUPERVISOR"
  | "PEER"
  | "PCP"
  | "CARE_MANAGER"
  | "PRESCRIBE";

export type ProviderPreferences = Preferences & {
  is_sms_enabled?: boolean;
};

export type AgeRange = { max: number; min: number };

export type StateToPractice = {
  expirationDate: string;
  licenseNumber: string;
  state: string;
};

export type ProviderAvailability = Partial<
  Record<Weekday, { enabled: boolean; slots: [string, string][] }>
>;

type ProviderAddress = { city: string; state: string; street: string; zip_code: string };

type UniversalAccessReason = { on_call: boolean; other: boolean; other_free_text: string };

export type ProviderProfile = ProfileCommon & {
  accept_new_patients: boolean;
  accepted_age_range: AgeRange[];
  accepted_insurance: InsuranceCarrier[];
  address: ProviderAddress;
  availability?: ProviderAvailability;
  availability_lookahead: { "30_days": number; "7_days": number; "90_days": number };
  availability_lookahead_start_date: string;
  average_patient_adherence: number;
  cp_credentials: string;
  group_type: string;
  /** SSO identity provider identifier */
  idp?: string;
  idp_logout_url?: string;
  intake_practice_ids: UUID[];
  is_edit_care_plan_enabled: boolean;
  is_universal_access_enabled: boolean;
  is_universal_access_enabled_modified_by_name: string;
  is_universal_access_enabled_modified_by_username: UUID;
  is_universal_access_enabled_modified_timestamp: string;
  is_universal_access_enabled_reason: UniversalAccessReason;
  /** Full display name, e.g. "Dr. Jane Smith, MD" */
  name: string;
  notes_in_draft_status: number;
  npi_number: string;
  omnisearch_enabled: boolean;
  patients_included_in_adherence_calculation: number;
  preferences: ProviderPreferences;
  preferred_name: Nullable<string>;
  preferred_pronouns: Nullable<string>;
  provider_type: ProviderType;
  role: Role;
  states_to_practice: StateToPractice[];
  total_patients: number;
};

export type CreateProviderBody = {
  accept_new_patients: boolean;
  accepted_age_range?: AgeRange[];
  address: PracticeAddress;
  cp_credentials: string;
  group_type: "provider";
  intake_practice_ids: Nullable<UUID[]>;
  mobile: string;
  name: string;
  npi_number: string;
  practice: string;
  practice_id: UUID;
  practice_ids: UUID[];
  provider_type: string;
  Username: string;
};

export type UpdateProviderBody = {
  accept_new_patients?: boolean;
  accepted_age_range?: AgeRange[];
  accepted_insurance?: string[];
  cp_credentials?: string;
  intake_practice_ids?: UUID[];
  mobile?: string;
  name?: string;
  npi_number?: string;
  preferences?: ProviderPreferences;
  states_to_practice?: StateToPractice[];
};

/** Narrows a profile value to ProviderProfile at runtime */
export function isProviderProfile(profile: unknown): profile is ProviderProfile {
  return typeof profile === "object" && profile !== null && "provider_type" in profile;
}
